import React from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import configData from "../../config/config.json";
import { validatePassword } from "../common/function/userValidation.js";
import BackButton from "../common/BackButton";
import Alert from "../common/modal/Alert";
import Loading from "../common/modal/Loading";
import { LOADING_STATUES, ALERT_TYPES, PASSWORD_REQUIREMENTS_HINT, PASSWORD_RULES } from "../constants/constants";
import { warningSmallIcon } from "../../img/font_awesome/font_awesome";

export default function CurrentUserChangePasswordSmall() {
  const history = useHistory();
  const isNarrowScreen = window.innerWidth <= 360;
  const countyName = useSelector((state) => state.countyConfigCommonReducer.countyName);
  const appTitle = useSelector((state) => state.countyConfigCommonReducer.appTitle);

  const [password, setPassword] = React.useState("");
  const [passwordConfirm, setPasswordConfirm] = React.useState("");
  const [errors, setErrors] = React.useState({});
  const [loadingStatus, setLoadingStatus] = React.useState("");
  const [showAlert, setShowAlert] = React.useState(false);
  const [alertMessage, setAlertMessage] = React.useState("");
  const [alertType, setAlertType] = React.useState(ALERT_TYPES.OK);
  const [showPassword, setShowPassword] = React.useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = React.useState(false);

  const handleSubmit = (event) => {
    event.preventDefault();

    const validationErrors = validatePassword({ password, passwordConfirm });
    if (validationErrors && Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoadingStatus(LOADING_STATUES.LOADING);

    axios.post(configData.LANDNOTIFICATION_SERVER_URL + "currentUser/password", { password }, { withCredentials: true })
      .then(() => {
        setLoadingStatus("");
        setAlertType(ALERT_TYPES.OK);
        setShowAlert(true);
        setAlertMessage("Password updated.");
      }, (error) => {
        setLoadingStatus("");
        setAlertType(ALERT_TYPES.WARNING);
        setShowAlert(true);
        setAlertMessage(error.response.data.message);
      });
  };

  const hideAlert = () => {
    setPassword("");
    setPasswordConfirm("");
    setAlertType(ALERT_TYPES.OK);
    setShowAlert(false);
    setAlertMessage("");
    history.goBack();
  };

  const handleBack = () => {
    history.goBack();
  };

  return (
    <div>
      {/* Banner */}
      <div style={styles.banner}>
        <div style={styles.logoContainer}>
          <BackButton onClick={handleBack} ariaLabel="Back" />
        </div>

        <div style={styles.titleContainer}>
          <div
            style={{
              ...styles.title,
              fontSize: isNarrowScreen ? "16px" : "20px",
            }}
          >
            {countyName}
          </div>
          <div
            style={{
              ...styles.subtitle,
              fontSize: isNarrowScreen ? "13px" : "16px",
            }}
          >
            {appTitle}
          </div>
        </div>

        <div style={styles.right} />
      </div>

      {/* Content */}
      <div style={styles.content}>
        <h2
          style={{
            ...styles.pageTitle,
            fontSize: isNarrowScreen ? "18px" : "20px",
          }}
        >
          Change Password
        </h2>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div>
            <label style={styles.label} htmlFor="password">New Password</label>
            <div style={styles.passwordRow}>
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                passwordrules={PASSWORD_RULES}
                value={password}
                onChange={(event) => { setPassword(event.target.value); setErrors({}); }}
                style={styles.passwordInput}
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} style={styles.showHideButton}>
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
            <div style={styles.hint}>{PASSWORD_REQUIREMENTS_HINT}</div>
            {errors.password && <div style={styles.error}>{warningSmallIcon} {errors.password}</div>}
          </div>

          <div>
            <label style={styles.label} htmlFor="passwordConfirm">Confirm Password</label>
            <div style={styles.passwordRow}>
              <input
                id="passwordConfirm"
                type={showPasswordConfirm ? "text" : "password"}
                autoComplete="new-password"
                value={passwordConfirm}
                onChange={(event) => { setPasswordConfirm(event.target.value); setErrors({}); }}
                style={styles.passwordInput}
              />
              <button type="button" onClick={() => setShowPasswordConfirm(!showPasswordConfirm)} style={styles.showHideButton}>
                {showPasswordConfirm ? "Hide" : "Show"}
              </button>
            </div>
            {errors.passwordConfirm && <div style={styles.error}>{warningSmallIcon} {errors.passwordConfirm}</div>}
          </div>

          <button type="submit" disabled={loadingStatus === LOADING_STATUES.LOADING} style={styles.submitButton}>
            {loadingStatus === LOADING_STATUES.LOADING ? "Updating..." : "Update"}
          </button>
        </form>
      </div>

      <Loading loadingIndicator={loadingStatus === LOADING_STATUES.LOADING} />
      <Alert show={showAlert} hideAlert={hideAlert} message={alertMessage} type={alertType} />
    </div>
  );
}

const styles = {
  banner: {
    display: "grid",
    gridTemplateColumns: "auto 1fr auto",
    alignItems: "center",
    backgroundColor: "#114E6B",
    color: "white",
    padding: "14px 16px",
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    boxSizing: "border-box",
    overflowX: "hidden",
    zIndex: 1000,
  },

  logoContainer: {
    width: "40px",
    height: "40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },


  titleContainer: {
    marginLeft: "8px",
    textAlign: "left",
  },

  title: {
    fontWeight: "700",
    fontSize: "20px",
    lineHeight: "1.2",
    color: "white",
  },

  subtitle: {
    fontSize: "16px",
    letterSpacing: "0.5px",
    opacity: 0.9,
    color: "white",
  },

  right: {
    justifySelf: "end",
    width: "40px",
  },

  content: {
    marginTop: "70px",
    padding: "16px",
  },

  pageTitle: {
    marginBottom: "20px",
    color: "#114E6B",
  },

  form: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },

  label: {
    display: "block",
    fontSize: "13px",
    fontWeight: "bold",
    color: "#114E6B",
    marginBottom: "4px",
  },

  input: {
    width: "100%",
    padding: "10px",
    fontSize: "16px",
    borderRadius: "6px",
    border: "1px solid #ccc",
    boxSizing: "border-box",
  },

  passwordRow: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
  },

  passwordInput: {
    flex: 1,
    padding: "10px",
    fontSize: "16px",
    borderRadius: "6px",
    border: "1px solid #ccc",
    boxSizing: "border-box",
  },

  showHideButton: {
    backgroundColor: "white",
    color: "#114E6B",
    padding: "10px 12px",
    borderRadius: "6px",
    border: "1px solid #114E6B",
    fontWeight: "bold",
    fontSize: "13px",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },

  error: {
    color: "red",
    fontWeight: "bold",
    fontSize: "14px",
    display: "flex",
    alignItems: "center",
    gap: "4px",
    marginTop: "2px",
  },

  hint: {
    color: "#6c757d",
    fontSize: "12px",
    marginTop: "4px",
  },

  submitButton: {
    width: "100%",
    backgroundColor: "#114E6B",
    color: "white",
    padding: "10px",
    borderRadius: "6px",
    border: "none",
    fontWeight: "bold",
    cursor: "pointer",
  },
};
